import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListStateShell } from '../ListStateShell';
import { useListFetch } from '../useListFetch';
import { ContactCreateForm } from '../ContactCreateForm';

/**
 * AddLeaseForm — brief §2's "Add lease on a vacant unit". Shown inside UnitDrillInOverlay when a unit
 * has no active lease. Assigns a tenant (match-or-create against the existing Contacts rolodex — the
 * same identity the bulk-import wedge dedups on), a rent and a term, then POSTs /api/leases
 * ({ unitId, tenantLeadId, startDate, endDate, rent }). The term is entered as a month count and the
 * endDate computed from it — createLease requires end >= start, which a positive term guarantees.
 *
 * Tenant selection reuses the shipped pieces rather than a new picker widget: the contacts list is the
 * same GET /api/customers?kind=contact the Contacts screen reads, and "create new" hands off to the
 * ratified ContactCreateForm (which POSTs /api/customers and returns the new leadId). No tenant is
 * ever auto-chosen; nothing is created silently.
 *
 * I2 §3 — also the Renew form, verbatim: a renewal POSTs the exact same /api/leases shape (a NEW Lease
 * row, never a mutation of the old one), so it reuses this component rather than inventing a second
 * lease-create UI. `initialTenant`/`initialRent`/`initialStartDate` prefill from the expiring lease
 * (all three stay editable — "Change" still swaps the tenant, the date/rent inputs are never locked);
 * `startDate`'s prefill is the ratified contiguity default (cmdr 2026-10-18) — the caller passes the
 * old lease's own endDate, never "today". `title`/`submitLabel` only override copy for that context.
 */

interface Contact { id: string; leadId: string; name: string; email: string | null; phone: string | null }

const addMonths = (iso: string, months: number): string => {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return d.toISOString();
};

export const AddLeaseForm: React.FC<{
  unitId: string;
  onCreated: () => void;
  onCancel: () => void;
  initialTenant?: { leadId: string; name: string | null } | null;
  initialRent?: number;
  initialStartDate?: string; // yyyy-mm-dd; I2 §3's contiguity default (the expiring lease's own endDate)
  title?: string;
  submitLabel?: string;
}> = ({ unitId, onCreated, onCancel, initialTenant = null, initialRent, initialStartDate, title, submitLabel }) => {
  const { t } = useTranslation('common');
  const [tenant, setTenant] = useState<{ leadId: string; name: string | null } | null>(initialTenant);
  const [creatingContact, setCreatingContact] = useState(false);
  const [query, setQuery] = useState('');
  const [rent, setRent] = useState(initialRent !== undefined ? String(initialRent) : '');
  const [startDate, setStartDate] = useState(() => initialStartDate ?? new Date().toISOString().slice(0, 10));
  const [termMonths, setTermMonths] = useState('12');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const list = useListFetch<Contact[]>('/api/customers?kind=contact', {
    map: (rows) => (rows as any[]).map((l) => ({
      id: l.id, leadId: l.leadId ?? l.id,
      name: l.name || l.firstName || '',
      email: l.email ?? null, phone: l.phone ?? null,
    })),
  });
  const contacts = list.data ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => `${c.name} ${c.email ?? ''} ${c.phone ?? ''}`.toLowerCase().includes(q));
  }, [contacts, query]);

  if (creatingContact) {
    // The ratified match-or-create precedent — on success its returned leadId becomes the tenant.
    return (
      <div data-testid="add-lease-create-contact">
        <ContactCreateForm
          onCreated={(leadId) => { setTenant({ leadId, name: null }); setCreatingContact(false); }}
          onCancel={() => setCreatingContact(false)}
        />
      </div>
    );
  }

  const rentNum = Number(rent.replace(/[^0-9.]/g, ''));
  const rentValid = rent.trim() !== '' && Number.isFinite(rentNum);
  const canSubmit = !!tenant && rentValid && !!startDate;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting || !tenant) return;
    setSubmitting(true);
    setError(null);
    const months = Math.max(1, Math.floor(Number(termMonths) || 12));
    try {
      const res = await fetch('/api/leases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          unitId,
          tenantLeadId: tenant.leadId,
          rent: rentNum,
          startDate: new Date(startDate).toISOString(),
          endDate: addMonths(startDate, months),
        }),
      });
      // I5/§5/AC5.4 — the overlap guard's own refusal gets its own honest message, distinct from the
      // generic create-failed copy (the unit/tenant/dates were all fine; the window just collides).
      if (res.status === 409) { setError(t('portfolio.setup.addLease.overlapError')); return; }
      if (!res.ok) { setError(t('portfolio.setup.addLease.error')); return; }
      onCreated();
    } catch {
      setError(t('portfolio.setup.addLease.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'h-10 px-3 rounded-lg border border-border-neutral text-sm w-full';

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 mt-2" data-testid="add-lease-form">
      {title && <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide" data-testid="add-lease-title">{title}</div>}
      {/* Tenant — selected, or a searchable list + create-new */}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-text-muted">{t('portfolio.setup.addLease.tenant')}</span>
        {tenant ? (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-border-neutral px-3 h-10">
            <span className="text-sm font-semibold text-text-strong truncate" data-testid="add-lease-tenant-selected">
              {tenant.name ?? t('portfolio.setup.addLease.newContactSelected')}
            </span>
            <button type="button" data-testid="add-lease-tenant-change" onClick={() => setTenant(null)}
              className="text-xs font-semibold text-primary underline shrink-0">
              {t('portfolio.setup.addLease.changeTenant')}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <input data-testid="add-lease-tenant-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t('portfolio.setup.addLease.searchPlaceholder')} dir="auto" className={inputCls} />
            <div className="border border-border-neutral rounded-lg max-h-40 overflow-y-auto" data-testid="add-lease-tenant-list">
              <ListStateShell status={list.status} testIdBase="add-lease-tenant" onRetry={list.retry}>
                {filtered.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-text-muted" data-testid="add-lease-tenant-empty">{t('portfolio.setup.addLease.noContacts')}</div>
                ) : (
                  filtered.map((c) => (
                    <button key={c.leadId} type="button" data-testid="add-lease-tenant-option"
                      onClick={() => setTenant({ leadId: c.leadId, name: c.name || c.email || c.phone || null })}
                      className="w-full text-start min-h-[44px] px-3 py-2 hover:bg-canvas border-b border-border-neutral last:border-b-0">
                      <div className="text-sm font-semibold text-text-strong truncate">{c.name || c.email || c.phone}</div>
                      {(c.email || c.phone) && <div className="text-[11px] text-text-muted truncate" dir="ltr">{c.email ?? c.phone}</div>}
                    </button>
                  ))
                )}
              </ListStateShell>
            </div>
            <button type="button" data-testid="add-lease-create-contact-open" onClick={() => setCreatingContact(true)}
              className="min-h-[44px] px-3 rounded-lg border border-primary/40 text-xs font-semibold text-primary hover:bg-primary/5 self-start">
              + {t('portfolio.setup.addLease.createNew')}
            </button>
          </div>
        )}
      </div>

      <label className="text-xs text-text-muted flex flex-col gap-1">
        {t('portfolio.setup.addLease.rent')}
        <input inputMode="decimal" data-testid="add-lease-rent" value={rent} onChange={(e) => setRent(e.target.value)} className={inputCls} dir="ltr" />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-text-muted flex flex-col gap-1">
          {t('portfolio.setup.addLease.startDate')}
          <input type="date" required data-testid="add-lease-start" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} dir="ltr" />
        </label>
        <label className="text-xs text-text-muted flex flex-col gap-1">
          {t('portfolio.setup.addLease.termMonths')}
          <input type="number" min="1" data-testid="add-lease-term" value={termMonths} onChange={(e) => setTermMonths(e.target.value)} className={inputCls} dir="ltr" />
        </label>
      </div>

      {!canSubmit && (tenant || rent) && (
        <div className="text-xs text-opt-rose" data-testid="add-lease-requirement">{t('portfolio.setup.addLease.requirement')}</div>
      )}
      {error && <div className="text-xs text-opt-rose" data-testid="add-lease-error">{error}</div>}

      <div className="flex items-center gap-2">
        <button type="submit" disabled={!canSubmit || submitting} data-testid="add-lease-submit"
          className="min-h-[44px] px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50">
          {submitLabel ?? t('portfolio.setup.addLease.submit')}
        </button>
        <button type="button" onClick={onCancel} data-testid="add-lease-cancel"
          className="min-h-[44px] px-4 rounded-lg text-sm font-semibold text-text-muted">
          {t('portfolio.setup.addLease.cancel')}
        </button>
      </div>
    </form>
  );
};
